<script lang="ts" setup>
import type { RouteRecordNormalized } from 'vue-router';
import type { WithPageMeta } from '#layers/spaces/public';
import type { FoldableBreadcrumbItem } from '#layers/ui/public';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from '#app/composables/router';
import { useWorkspaceEntityStore } from '#layers/runtime/public';
import { isSpacePageRoute, SpacePageSelector, translatePage, useCurrentPageNotRendered } from '#layers/spaces/public';
import WorkspaceChromeBreadcrumb from '#layers/ui-workspace/components/WorkspaceChromeBreadcrumb.vue';

const route = useRoute();
const i18n = useI18n();
const store = useWorkspaceEntityStore();

const TOP_LEVEL_PAGE_SLOT = 'top-level-page-selector';

const { currentPageNotRendered } = useCurrentPageNotRendered();
const spacePageRouteRecords = computed(() => route.matched.filter((record) => isSpacePageRoute(record)) as WithPageMeta[]);

const items = computed<FoldableBreadcrumbItem[]>(() => {
	const result: FoldableBreadcrumbItem[] = [];

	for (const [index, record] of spacePageRouteRecords.value.entries()) {
		const item = pageRouteRecordToBreadcrumb(record, index);
		if (item) result.push(item);
	}

	if (currentPageNotRendered.value) {
		result.push(pageToBreadcrumbItem(currentPageNotRendered.value, true));
	}

	return result;
});

interface SpaceRouteRecordData {
	id?: string;
	title: string;
	icon?: string | null;
	iconColor?: string | null;
}

function getSpaceRouteRecordData(record: WithPageMeta<RouteRecordNormalized>) {
	// Use rstore peekFirst to make the title reactive.
	const pageId = record.meta.page.id;

	const pageItem = pageId ? store.MonospacePage.peekFirst(pageId) : undefined;
	const resolvedData = pageItem ?? record.meta.page;

	if (Object.keys(resolvedData).length === 0) {
		return;
	}

	return {
		...resolvedData,
		isPageItem: !!pageItem,
	};
}

function pageRouteRecordToBreadcrumb(record: WithPageMeta<RouteRecordNormalized>, index: number): FoldableBreadcrumbItem | null {
	const data = getSpaceRouteRecordData(record);
	const breadcrumb = data ? pageToBreadcrumbItem({ ...record, ...data }, true) : null;

	const isTopLevelPageSelector = index === 0 && data?.isPageItem;

	if (breadcrumb) {
	// Special handling for the first page in the breadcrumb to show a page selector instead of the standard link
		if (isTopLevelPageSelector) {
			breadcrumb.slot = TOP_LEVEL_PAGE_SLOT;
			breadcrumb.switcher = true;
			breadcrumb.to = undefined;
		}
		else {
		// Nested page

			// Avoid showing the same title twice in a row
			const previousRecord = spacePageRouteRecords.value[index - 1];

			if (previousRecord) {
				const previousData = getSpaceRouteRecordData(previousRecord);

				if (previousData) {
					const previousTitle = translatePage(previousData, i18n).title;

					if (previousTitle && previousTitle === breadcrumb.label) {
						return null;
					}
				}
			}
		}
	}

	return breadcrumb;
}

function pageToBreadcrumbItem(data: SpaceRouteRecordData | (SpaceRouteRecordData & RouteRecordNormalized), link: boolean): FoldableBreadcrumbItem {
	const meta = translatePage(data, i18n);
	const to = link
		? {
				name: data.id ?? ('name' in data ? data.name : undefined),
				params: route.params,
			}
		: undefined;

	return {
		label: meta.title,
		icon: meta.icon || undefined,
		iconColor: meta.iconColor || undefined,
		to,
	};
}
</script>

<template>
	<WorkspaceChromeBreadcrumb
		:items
		:ui="{
			link: 'text-sm text-default',
		}"
	>
		<template #[TOP_LEVEL_PAGE_SLOT]="{ folded }">
			<SpacePageSelector :folded />
		</template>
	</WorkspaceChromeBreadcrumb>
</template>
